import plur from 'plur'
import {memo, useCallback} from 'react'
import {graphql, useFragment} from 'react-relay'

import LinkWithIcon from '../../../../common/LinkWithIcon/LinkWithIcon'
import RouterLinkWrapper from '../../../../common/RouterLinkWrapper/RouterLinkWrapper'

import type {TestsPreviewPanelCountersFragment$key} from './__generated__/TestsPreviewPanelCountersFragment.graphql'

import styles from './TestsPreviewPanelCounters.css'

const fragment = graphql`
  fragment TestsPreviewPanelCountersFragment on TestCounters {
    ignored
    success
    muted
    all
  }
`

type Props = {
  className?: string
  testsTabLink?: string
  countsKey: TestsPreviewPanelCountersFragment$key | null
}

function TestsPreviewPanelCounters({className, countsKey, testsTabLink}: Props) {
  const counts = useFragment(fragment, countsKey)
  const ignored = counts?.ignored ?? 0
  const success = counts?.success ?? 0
  const muted = counts?.muted ?? 0
  const all = counts?.all ?? 0

  const renderCount = useCallback(
    (type: 'all' | 'passed' | 'muted' | 'ignored', countByType: number, icon?: string | null) =>
      countByType > 0 && (
        <RouterLinkWrapper key={type}>
          <LinkWithIcon
            href={`${testsTabLink}&status=${type}`}
            className={styles.additionalInfoLink}
            iconClassName={type !== 'all' ? styles[`${type}Icon`] : undefined}
            icon={icon}
          >
            {type === 'all'
              ? `${countByType} ${plur('test', countByType)} in the current build`
              : `${countByType} ${type}`}
          </LinkWithIcon>
        </RouterLinkWrapper>
      ),
    [testsTabLink],
  )

  if (success === 0 && muted === 0 && ignored === 0) {
    return null
  }

  return (
    <div className={className} data-test-build-tests-preview={'counts'}>
      {[
        success !== all && muted !== all && ignored !== all ? renderCount('all', all) : null,
        renderCount('passed', success, 'checkmark'),
        renderCount('muted', muted, 'mute'),
        renderCount('ignored', ignored, 'ignored'),
      ].filter(Boolean)}
    </div>
  )
}

export default memo(TestsPreviewPanelCounters)
